import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import LayoutWithSidebar from './components/Layout/LayoutWithSidebar';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { FuturesWebSocketProvider, useFuturesQuotes } from './contexts/FuturesWebSocketContext';
import { OptionsWebSocketProvider } from './contexts/OptionsWebSocketContext';
import { useSequentialDataLoader } from './hooks/useSequentialDataLoader';

// Критичные компоненты загружаем сразу
import HomePageNew from './pages/HomePageNew';
import FloatingAIChat from './components/FloatingAIChat';

// Остальные страницы загружаем по требованию (Code Splitting)
const OptionsAnalyzer = lazy(() => import('./pages/OptionsAnalyzer'));
const AnalysisView = lazy(() => import('./pages/AnalysisView'));
const OptionsCalculatorBasic = lazy(() => import('./pages/OptionsCalculatorBasic'));
const SavedConfigurations = lazy(() => import('./pages/SavedConfigurations'));
const GradualStrategyCalculator = lazy(() => import('./components/GradualStrategyCalculator/GradualStrategyCalculator'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const TestChart = lazy(() => import('./pages/TestChart'));
const NewDeal = lazy(() => import('./pages/NewDeal'));
const DealsArchive = lazy(() => import('./pages/DealsArchive'));
const CryptoRating = lazy(() => import('./pages/CryptoRating'));

// Loading компонент для показа во время загрузки
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Загрузка...</p>
    </div>
  </div>
);

// Компонент для последовательной загрузки данных
// Порядок: market-status → all-contracts → (затем expirations, strikes в компонентах) → WebSocket
function ContractsLoader({ children }) {
  const { allContracts, isLoading, loadingStep } = useSequentialDataLoader();
  const { loadAllContracts } = useFuturesQuotes();
  const loadedRef = React.useRef(false);

  useEffect(() => {
    if (allContracts && !isLoading && !loadedRef.current) {
      console.log('📥 [ContractsLoader] Передаём контракты в провайдер (шаг:', loadingStep, ')');
      loadAllContracts(allContracts);
      loadedRef.current = true;
    }
  }, [allContracts, isLoading, loadAllContracts, loadingStep]);

  return children;
}

function App() {
  return (
    <WebSocketProvider>
      <FuturesWebSocketProvider>
        <OptionsWebSocketProvider>
          <ContractsLoader>
            <LayoutWithSidebar>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                <Route path="/" element={<HomePageNew />} />
                <Route path="/tools/options-analyzer" element={<OptionsAnalyzer />} />
                <Route path="/tools/options-calculator" element={<OptionsCalculatorBasic />} />
                <Route path="/tools/gradual-strategy-calculator" element={<GradualStrategyCalculator />} />
                <Route path="/tools/saved-configurations" element={<SavedConfigurations />} />
                <Route path="/tools/test-chart" element={<TestChart />} />
                <Route path="/tools/new-deal" element={<NewDeal />} />
                <Route path="/tools/deals-archive" element={<DealsArchive />} />
                <Route path="/tools/crypto-rating" element={<CryptoRating />} />
                <Route path="/components" element={<Navigate to="/settings?section=components" replace />} />
                <Route path="/analysis/:id" element={<AnalysisView />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </Suspense>
            
            {/* Глобальный плавающий AI-чат */}
            <FloatingAIChat />
          </LayoutWithSidebar>
          </ContractsLoader>
        </OptionsWebSocketProvider>
      </FuturesWebSocketProvider>
    </WebSocketProvider>
  );
}

function AppWithRouter() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <App />
    </Router>
  );
}

export default AppWithRouter;
